/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { cn } from '@repo/shared-client/lib/utils'
import { MoveDiagonal2Icon, RotateCwIcon, Trash2Icon } from 'lucide-react'
import type { CSSProperties, PointerEvent, ReactNode } from 'react'
import { RESIZE_HANDLE_SIZE_PX, ROTATE_HANDLE_SIZE_PX } from './image-editor-types'

type ImageEditorOverlayFrameProps = {
  selected: boolean
  style: CSSProperties
  removeLabel: string
  rotateLabel: string
  resizeLabel: string
  children: ReactNode
  onDragPointerDown: (event: PointerEvent<HTMLDivElement>) => void
  onRemove: () => void
  onRotatePointerDown: (event: PointerEvent<HTMLButtonElement>) => void
  onResizePointerDown: (event: PointerEvent<HTMLButtonElement>) => void
}

const ImageEditorOverlayFrame = ({
  selected,
  style,
  removeLabel,
  rotateLabel,
  resizeLabel,
  children,
  onDragPointerDown,
  onRemove,
  onRotatePointerDown,
  onResizePointerDown,
}: ImageEditorOverlayFrameProps) => (
  <div
    className={cn('absolute touch-none', selected ? 'z-20' : 'z-10')}
    onPointerDown={onDragPointerDown}
    style={style}
  >
    {children}
    {selected ? (
      <>
        <button
          aria-label={removeLabel}
          className="absolute top-0 right-0 z-30 flex size-10 translate-x-[30%] -translate-y-[130%] items-center justify-center rounded-full bg-destructive text-white shadow-lg ring-2 ring-white active:translate-x-[30%] active:-translate-y-[calc(130%-2px)]"
          onClick={(event) => {
            event.stopPropagation()
            onRemove()
          }}
          onPointerDown={(event) => event.stopPropagation()}
          type="button"
        >
          <Trash2Icon className="size-5" />
        </button>
        <button
          aria-label={rotateLabel}
          className="absolute bottom-0 left-0 z-30 flex -translate-x-[30%] translate-y-[130%] items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-2 ring-white"
          onPointerDown={onRotatePointerDown}
          style={{ width: ROTATE_HANDLE_SIZE_PX, height: ROTATE_HANDLE_SIZE_PX }}
          type="button"
        >
          <RotateCwIcon className="size-5" />
        </button>
        <button
          aria-label={resizeLabel}
          className="absolute right-0 bottom-0 z-30 flex translate-x-[40%] translate-y-[40%] cursor-nwse-resize items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-2 ring-white"
          onPointerDown={onResizePointerDown}
          style={{ width: RESIZE_HANDLE_SIZE_PX, height: RESIZE_HANDLE_SIZE_PX }}
          type="button"
        >
          <MoveDiagonal2Icon className="size-4" />
        </button>
      </>
    ) : null}
  </div>
)

export default ImageEditorOverlayFrame
